// components/JournalCsvImport.jsx
import React, { useEffect, useMemo, useState } from "react";
import Papa from "papaparse";
import { 
  FaExclamationTriangle, 
  FaArrowLeft, 
  FaFileCsv, 
  FaChartLine, 
  FaTable,
  FaGlobeAmericas
} from "react-icons/fa";
import { SiMetatrader, SiTradingview } from "react-icons/si"; // Optional icons if you have them, otherwise fallbacks used
import { db } from "../firebase";
import {
  doc, getDoc, updateDoc, collection, getDocs, addDoc
} from "firebase/firestore";
import { importCsvByPlatform } from "../lib/csvImporters";

// --- LOGO ASSETS ---
// Replace these paths with your actual image locations
import mt5Logo from "../assets/metatrader5.png"; 
import mt4Logo from "../assets/metatrader4.png"; 
import ninjaTraderLogo from "../assets/ninjatrader.png"; 
import tradovateLogo from "../assets/tradovate.png"; 
import rithmicLogo from "../assets/rithmic.png"; 
import tradingviewDark from "../assets/tradingview-logotype-light.png"; 
import tradingviewLight from "../assets/tradingview-logotype-dark.png";
import quantowerLogo from "../assets/quantower.png"; 
import trapoundLogo from "../assets/trapoundtop.png"; 
import topstepDark from "../assets/topstep-logo.svg"; 
import topstepLight from "../assets/topstepLight.png"; 
import mannyLogo from "../assets/manny.png"; 
import oandaLogo from "../assets/oanda.png"; 




// ---------- Shared helpers (aligned with Settings.jsx) ----------
const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

const parseSheetNumber = (value) => {
  if (typeof value === "number") return value;
  if (value == null) return 0;
  const s = String(value).trim();
  const isNegative = /^\(.*\)$/.test(s) || /^-/.test(s);
  const n = parseFloat(s.replace(/[^\d.]/g, "")) || 0;
  return isNegative ? -Math.abs(n) : n;
};


const TICK_SIZES = {
    'ES': 0.25, 'MES': 0.25, 'NQ': 0.25, 'MNQ': 0.25, 
    'YM': 1.0, 'MYM': 1.0, 'US30': 1.0,
    'GC': 0.1, 'MGC': 0.1, 'CL': 0.01, 'MCL': 0.01,
    'RTY': 0.1, 'M2K': 0.1
};

function formatDuration(totalSeconds) {
    if (totalSeconds <= 0) return "<1";
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    return hours > 0 ? `${hours}h ${minutes} mins` : `${minutes} mins`;
}

function getSession(entryDate) {
    const hour = entryDate.getHours();
    if (hour >= 18 || hour < 3) return 'Asian';
    if (hour >= 3 && hour < 8) return 'London';
    if (hour >= 8 && hour < 18) return 'New York';
    return '';
}

export function convertTradingViewToTrapound(tvCsvText, config) {
    const { product, entryStrategy, exitStrategy, timeframe, accountType, startingBalance } = config;
    
    // Parse the raw TV CSV
    const parsed = Papa.parse(tvCsvText, { header: true, skipEmptyLines: true });
    const tvData = parsed.data;

    // Group by 'Trade number'
    const grouped = {};
    tvData.forEach(row => {
        const tradeNum = row['Trade number'];
        if (!tradeNum) return;
        if (!grouped[tradeNum]) grouped[tradeNum] = [];
        grouped[tradeNum].push(row);
    });

    let trades = [];

    // Change this line:
        Object.entries(grouped).forEach(([tradeNum, group]) => {
            const entryRow = group.find(r => r['Type'] && r['Type'].includes('Entry'));
            const exitRow = group.find(r => r['Type'] && r['Type'].includes('Exit'));
            
            if (!entryRow || !exitRow) return; // Skip open trades

            // 1. Change the position mapping to Buy/Sell
        const position = (entryRow['Signal'] || '').includes('Long') ? "Buy" : "Sell";
        
        const entryTime = new Date(entryRow['Date and time']);
        const exitTime = new Date(exitRow['Date and time']);
        
        const timeDeltaSeconds = (exitTime - entryTime) / 1000;
        const durationStr = formatDuration(timeDeltaSeconds);

        const priceCol = Object.keys(entryRow).find(c => c.includes('Price')) || 'Price';
        const pnlCol = Object.keys(exitRow).find(c => c.includes('Net PnL')) || 'Net PnL';

        const entryPrice = parseFloat(entryRow[priceCol]);
        const exitPrice = parseFloat(exitRow[priceCol]);
        const netPnl = parseFloat(exitRow[pnlCol]);

        let priceDiff = exitPrice - entryPrice;
        
        // 2. Make sure this checks for "Sell" instead of "Short" so the math stays accurate
        if (position === "Sell") priceDiff = -priceDiff;

            let pips = "";
            let ticks = "";

            if (accountType.toLowerCase() === 'forex') {
                pips = product.toUpperCase().includes('JPY') 
                    ? (priceDiff * 100).toFixed(1) 
                    : (priceDiff * 10000).toFixed(1);
            } else if (accountType.toLowerCase() === 'futures') {
                const tickSize = TICK_SIZES[product.toUpperCase()] || 0.25;
                ticks = Math.round(priceDiff / tickSize);
            }

            const result = netPnl > 0 ? "Won" : "Loss";

            trades.push({
                // Update this line to use the TradingView trade number:
                'Trade #': Number(tradeNum), 
                'Account Type': accountType,
                'Product': product,
                'Position': position,
                'Size': entryRow['Size (qty)'],
                'Date': entryTime.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }),
                'Entry Time': entryTime.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' }),
                'Exit Time': exitTime.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' }),
                'Entry Price': entryPrice,
                'Exit Price': exitPrice,
                'Duration': durationStr,
                'Actual Reward': netPnl,
                'Result': result,
                'Win': result === 'Won' ? 'TRUE' : 'FALSE',
                'Loss': result === 'Loss' ? 'TRUE' : 'FALSE',
                'Ticks': ticks,
                'Pips': pips,
                'Session': getSession(entryTime),
                'Entry Strategy': entryStrategy,
                'Exit Strategy': exitStrategy,
                'Timeframe': timeframe,
                '_raw_exit_time': exitTime.getTime() // Temp sorting key
            });
        });

    // Sort chronologically (oldest first) for running balance
    trades.sort((a, b) => a._raw_exit_time - b._raw_exit_time);

    let currentBalance = parseFloat(startingBalance);

    trades = trades.map(trade => {
        const netProfit = parseFloat(trade['Actual Reward']);
        trade['Balance Before Trade'] = currentBalance.toFixed(2);
        
        const truePercentGrowth = (netProfit / currentBalance) * 100;
        trade['Actual Percent Growth'] = `${truePercentGrowth.toFixed(2)}%`;
        
        currentBalance += netProfit;
        trade['Actual Balance'] = currentBalance.toFixed(2);
        
        return trade;
    });

    // Re-sort descending (newest first)
    trades.sort((a, b) => b._raw_exit_time - a._raw_exit_time);

    // Clean up temp key
    trades.forEach(t => delete t._raw_exit_time);

    return trades;
}

// ... [Keep existing helper functions: toGoogleCsvUrl, fpTrapound, formatDate, toAmPm, splitDateTime, calculateDuration, getProduct, findValueByLabel] ...
// (For brevity, assuming standard helpers from previous file are present here. 
//  If you copy-paste, ensure formatDate, toAmPm, etc., are included from your original file)
function formatDate(val) {
  if (!val) return "";
  const d = new Date(val);
  return isNaN(d) ? "" : d.toISOString().slice(0, 10);
}
function toAmPm(timeStr) {
  if (!timeStr) return "";
  const [hour, min] = timeStr.split(":").map(x => parseInt(x, 10));
  if (isNaN(hour) || isNaN(min)) return "";
  const ampm = hour >= 12 ? "PM" : "AM";
  const hh = hour % 12 || 12;
  return `${hh}:${min.toString().padStart(2, "0")} ${ampm}`;
}
function splitDateTime(str) {
  if (!str) return { date: "", time: "" };
  const parts = String(str).split(" ");
  if (parts.length < 2) return { date: parts[0], time: "" };
  return { date: parts[0], time: parts[1] };
}
function calculateDuration(start, end) {
  if (!start || !end) return "";
  const s = new Date(start);
  const e = new Date(end);
  if (isNaN(s) || isNaN(e) || e < s) return "";
  const secs = Math.floor((e - s) / 1000);
  const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60), ss = secs % 60;
  return `${h}:${String(m).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
}
function getProduct(val = "") {
  const s = String(val);
  if (!s) return "";
  if (s[0] === "M") return s.slice(0, 3);
  return s.slice(0, 2);
}
function findValueByLabel(sheetData, label) {
  const lower = String(label || "").toLowerCase();
  for (const row of sheetData) {
    if (!row) continue;
    for (let i = 0; i < row.length - 1; i++) {
      const cell = row[i];
      if (typeof cell === "string" && cell.toLowerCase().includes(lower)) {
        return row[i + 1];
      }
    }
  }
  return null;
}

// --- NEW: Sync Master Balance after CSV Import ---
async function updateMasterBalanceFromCSV(userId, accountId) {
  if (!userId || !accountId) return;
  try {
    // 1. Fetch settings to know if it's a Prop account and get risk parameters
    const s = await getAccountSettings(userId, accountId);
    const settingsObj = s?.settings || s || {};
    const isPropAcct = Boolean(settingsObj.isPropFirm || settingsObj.isProp || settingsObj.isPropFunded);
    const initialBalance = Number(settingsObj["Initial Balance"] || 0);
    const maxLossLimit = Number(settingsObj["Maximum Loss Limit"] || 0);

    // 2. Fetch all rows to find the newest one
    const rows = await fetchJournalRows(userId, accountId);
    if (!rows || rows.length === 0) return;

    // 3. Sort by Date (newest first), fallback to Trade #
    rows.sort((a, b) => {
      const timeA = new Date(a["Date"]).getTime() || 0;
      const timeB = new Date(b["Date"]).getTime() || 0;
      if (timeB !== timeA) return timeB - timeA;
      const numA = parseInt(a["Trade #"], 10) || 0;
      const numB = parseInt(b["Trade #"], 10) || 0;
      return numB - numA;
    });

    const newestTrade = rows[0];
    const latestBalance = Number(newestTrade["Actual Balance"]); // e.g., 10250

    // 4. Update the master account document
    if (!isNaN(latestBalance)) {
      const patch = {
        "Running Balance": latestBalance,
        "Beginning Balance": latestBalance,
        "lastUpdated": new Date().toISOString(),
        "settings.Running Balance": latestBalance,
        "settings.Beginning Balance": latestBalance
      };
      
      // Calculate True Effective Prop Balance
      if (isPropAcct && initialBalance > 0 && maxLossLimit > 0) {
        // Find the blown-out floor (e.g., 10000 - 1000 = 9000)
        const blownAt = Math.max(maxLossLimit, initialBalance - maxLossLimit);
        // Effective balance = Ledger - Floor (e.g., 10250 - 9000 = 1250)
        const calculatedPropBalance = Math.max(0, latestBalance - blownAt);
        
        patch["Prop Running Balance"] = calculatedPropBalance;
        patch["settings.Prop Running Balance"] = calculatedPropBalance;
      } else if (isPropAcct) {
        // Fallback if initial balance wasn't set yet
        patch["Prop Running Balance"] = latestBalance;
        patch["settings.Prop Running Balance"] = latestBalance;
      }
      
      await updateAccountSettings(userId, accountId, patch);
      console.log(`[CSV Import] Master balance synced to $${latestBalance}`);
    }
  } catch (err) {
    console.error("Failed to sync master balance after CSV import:", err);
  }
}

// --- Firestore helpers ---
async function getAccountSettings(userId, accountId) {
  if (!userId || !accountId) return null;
  const ref = doc(db, "users", userId, "accounts", accountId);
  const snap = await getDoc(ref);
  return snap.exists() ? snap.data() : null;
}
async function updateAccountSettings(userId, accountId, patch) {
  if (!userId || !accountId) return;
  const ref = doc(db, "users", userId, "accounts", accountId);
  await updateDoc(ref, patch);
}
async function appendTrade(userId, accountId, trade) {
  if (!userId || !accountId) return;
  const j = collection(db, "users", userId, "accounts", accountId, "journal");
  await addDoc(j, trade);
}
async function fetchJournalRows(userId, accountId) {
  if (!userId || !accountId) return [];
  const j = collection(db, "users", userId, "accounts", accountId, "journal");
  const snap = await getDocs(j);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// ---------- Platform Config ----------
const PLATFORMS = [
  { value: "topstep",           label: "TopStepX", type: "futures", color: "bg-red-600" },
  { value: "tradovate",         label: "Tradovate", type: "futures", color: "bg-blue-600" },
  { value: "rithmic",           label: "Rithmic", type: "futures", color: "bg-orange-500" },
  { value: "ninjatrader",       label: "NinjaTrader", type: "futures", color: "bg-green-600" },
  { value: "mt4",               label: "MetaTrader 4", type: "forex", logo: true }, // Special flag for logo
  { value: "mt5",               label: "MetaTrader 5", type: "forex", logo: true }, // Special flag for logo
  { value: "tradingview",       label: "TradingView", type: "both", color: "bg-black dark:bg-gray-700" },
  { value: "tradingview_strategy", label: "TradingView Strategy", type: "both", color: "bg-black dark:bg-gray-700" },
  { value: "trapound",          label: "Trapound", type: "both", color: "bg-purple-600" },
  { value: "quantower",           label: "Quantower", type: "futures", color: "bg-orange-500" },
  { value: "mannysgooglesheet", label: "Manny’s Sheet", type: "forex", color: "bg-green-700" },
];

// Helper to render the specific visual for a platform card
// Helper to render the specific visual for a platform card
function PlatformLogo({ platform, className }) {
  
  // 1. Standard Size (for most logos)
  // max-h-12 = 3rem (48px) height limit
  const standardClasses = "max-h-12 max-w-full object-contain drop-shadow-sm";

  // 2. Large Size (Specifically for Trapound)
  // max-h-20 = 5rem (80px) height limit (Adjust this number up/down as needed)
  const largeClasses = "max-h-20 max-w-full object-contain drop-shadow-sm";

  // Helper wrapper to ensure centering and padding
  const Wrapper = ({ children }) => (
    <div className={`flex items-center justify-center w-full h-full px-2 ${className}`}>
      {children}
    </div>
  );

  // --- TRADINGVIEW (Light & Dark Mode) ---
  if (platform.value === "tradingview" || platform.value === "tradingview_strategy") {
    return (
      <Wrapper>
        {/* Light Mode: Hidden in dark */}
        <img 
          src={tradingviewLight} 
          alt="TradingView" 
          className={`${standardClasses} dark:hidden`} 
        />
        {/* Dark Mode: Hidden in light, Block in dark */}
        <img 
          src={tradingviewDark} 
          alt="TradingView" 
          className={`${standardClasses} hidden dark:block`} 
        />
      </Wrapper>
    );
  }

  if (platform.value === "topstep") {
    return (
      <Wrapper>
        {/* Light Mode: Hidden in dark */}
        <img 
          src={topstepLight} 
          alt="TopStepX" 
          className={`${standardClasses} dark:hidden`} 
        />
        {/* Dark Mode: Hidden in light, Block in dark */}
        <img 
          src={topstepDark} 
          alt="TopStepX" 
          className={`${standardClasses} hidden dark:block`} 
        />
      </Wrapper>
    );
  }

  // --- TRAPOUND (Bigger) ---
  if (platform.value === "trapound") {
    return (
      <Wrapper>
        <img 
          src={trapoundLogo} 
          alt="Trapound" 
          className={largeClasses} // Using the larger class here
        />
      </Wrapper>
    );
  }
  // --- NINJATRADER ---
    if (platform.value === "ninjatrader") {
      return (
        <Wrapper>
          <img src={ninjaTraderLogo} alt="NinjaTrader" className={standardClasses} />
        </Wrapper>
      );
    }
    if (platform.value === "quantower") {
      return (
        <Wrapper>
          <img src={quantowerLogo} alt="NinjaTrader" className={standardClasses} />
        </Wrapper>
      );
    }

    // --- MANNY'S SHEET ---
    if (platform.value === "mannysgooglesheet") {
      return (
        <Wrapper>
          <img src={mannyLogo} alt="Manny’s Sheet" className={standardClasses} />
        </Wrapper>
      );
    }

    // --- TRADOVATE ---
    if (platform.value === "tradovate") {
      return (
        <Wrapper>
          <img src={tradovateLogo} alt="Tradovate" className={standardClasses} />
        </Wrapper>
      );
    }

    // --- RITHMIC ---
    if (platform.value === "rithmic") {
      return (
        <Wrapper>
          <img src={rithmicLogo} alt="Rithmic" className={standardClasses} />
        </Wrapper>
      );
    }

    if (platform.value === "mt4") {
      return (
        <Wrapper>
          <img src={mt4Logo} alt="MT4" className={standardClasses} />
        </Wrapper>
      );
    }

    if (platform.value === "mt5") {
      return (
        <Wrapper>
          <img src={mt5Logo} alt="MT5" className={standardClasses} />
        </Wrapper>
      );
    }

  // Fallback: Initials or Generic Icon with background color
  return (
    <div className={`w-full h-full flex items-center justify-center rounded-lg shadow-inner ${platform.color || "bg-gray-500"}`}>
      <span className="text-white font-bold text-2xl tracking-wider drop-shadow-md">
        {platform.label.slice(0, 2).toUpperCase()}
      </span>
    </div>
  );
}

function ImportResultModal({ open, result, onAcknowledge, onDismiss }) {
  if (!open) return null;
  const ok = result?.ok;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className={`text-lg font-bold ${ok ? "text-green-700 dark:text-green-400" : "text-red-700 dark:text-red-400"}`}>
            {ok ? "Import Complete" : "Import Failed"}
          </h3>
        </div>

        <div className="px-5 py-4 text-sm text-gray-800 dark:text-gray-100">
          {ok ? (
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Imported</span>
                <span className="font-semibold">{result.imported}</span>
              </div>
              <div className="flex justify-between">
                <span>Skipped (duplicates/mismatch)</span>
                <span className="font-semibold">{result.skipped}</span>
              </div>
              {result.note ? (
                <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">{result.note}</div>
              ) : null}
            </div>
          ) : (
            <div className="space-y-2">
              <div className="text-red-700 dark:text-red-400 font-semibold">We couldn’t import your file.</div>
              <div className="text-xs text-gray-600 dark:text-gray-300">
                {result?.error || "Please check the file and try again."}
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          {ok ? (
            <button type="button" onClick={onAcknowledge} className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white font-semibold">
              Done
            </button>
          ) : (
            <button type="button" onClick={onDismiss} className="px-4 py-2 rounded-md bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-100">
              OK
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Component ----------
export default function JournalCsvImport({
  userId,
  accountId,
  onDone,
  allowedPlatforms, // optional list of values
}) {
  const [platform, setPlatform] = useState("");
  const [file, setFile] = useState(null);
  const [finalBalance, setFinalBalance] = useState("");
  const [importing, setImporting] = useState(false);
  const [importLog, setImportLog] = useState([]);
  const [importDuplicates, setImportDuplicates] = useState([]);
  const [googleSheetUrl, setGoogleSheetUrl] = useState("");
  const [resultModal, setResultModal] = useState(null);

  // Account settings snapshot
  const [accountDoc, setAccountDoc] = useState(null);
  const settings = accountDoc?.settings || {};
  const [showTVModal, setShowTVModal] = useState(false);
const [tvConfig, setTvConfig] = useState({
    product: '',
    entryStrategy: '',
    exitStrategy: '',
    timeframe: '',
    accountType: 'Futures', // Default
    startingBalance: 2000.0 // Default
});
const [tvFile, setTvFile] = useState(null);

const handleTVImport = async () => {
    if (!tvFile) return alert("Please select a TradingView CSV file.");
    
    const reader = new FileReader();
    reader.onload = async (e) => {
        const text = e.target.result;
        // Convert to Trapound JSON format
        const convertedTrades = convertTradingViewToTrapound(text, tvConfig);
        
        // Pass these trades to your existing upload/save pipeline
        // (e.g., iterating through convertedTrades and calling addDoc/appendTrade)
        console.log("Converted Trades Ready for Upload: ", convertedTrades);
        
        setShowTVModal(false);
        // await onDone(); // Trigger refresh
    };
    reader.readAsText(tvFile);
};

  useEffect(() => {
    (async () => {
      try {
        const doc = await getAccountSettings(userId, accountId);
        setAccountDoc(doc || null);
      } catch (e) { /* non-fatal */ }
    })();
  }, [userId, accountId]);

  // Resolve account type (Forex vs Futures) -> filter platforms
  const acctTypeRaw = useMemo(() => {
    const s = settings || {};
    const t1 = String(accountDoc?.type || "").toLowerCase();
    const t2 = String(s?.type || "").toLowerCase();
    const t3 = String(s?.["Trade Mode"] || "").toLowerCase();
    const t4 = String(s?.["Account Type"] || "").toLowerCase();
    return t1 || t2 || t3 || t4;
  }, [accountDoc, settings]);

  const isForex = /\bfx\b|\bforex\b/.test(acctTypeRaw);

  // Compute available options based on props OR account type
  const availableOptions = useMemo(() => {
    let base = PLATFORMS;
    
    // If props passed specific allow list, filter strictly
    if (allowedPlatforms && allowedPlatforms.length > 0) {
      const allowedSet = new Set(allowedPlatforms.map(p => (typeof p === 'string' ? p : p.value)));
      base = base.filter(p => allowedSet.has(p.value));
    } else {
      // Otherwise use account type heuristic
      if (isForex) {
        base = base.filter(p => ["forex", "both"].includes(p.type));
      } else {
        base = base.filter(p => ["futures", "both"].includes(p.type));
      }
    }
    return base;
  }, [allowedPlatforms, isForex]);

  const isMannyGoogleSheet = platform === "mannysgooglesheet";
  const isTVStrategy = platform === "tradingview_strategy";
  const acceptTypes =
  platform === "mt5"
    ? ".csv,.xlsx"
    : platform === "mt4"
    ? ".csv,.xls,.xlsx,.htm,.html"
    : ".csv";
  const isPropFirm = Boolean(settings?.isPropFirm || settings?.isProp || settings?.isPropFunded);

  // ... [Keep importMT5 function] ...
  async function importMT5(file, journalRows) {
    let rowsAA = [];
    const isXlsx = /\.xlsx?$/i.test(file?.name || "");

    if (isXlsx) {
      // dynamic import keeps bundle small
      let XLSX;
      try {
        XLSX = (await import("xlsx")).default || (await import("xlsx"));
      } catch (e) {
        // Bubble up so handleImport can show the error modal
        throw new Error(
          "MT5: XLSX reader not available. Install the 'xlsx' package or export your MT5 report as CSV."
        );
      }
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf);
      const ws = wb.Sheets[wb.SheetNames[0]];
      rowsAA = XLSX.utils.sheet_to_json(ws, {
        header: 1,
        raw: true,
        blankRows: false,
        defval: null,
      });
    } else {
      const text = await file.text();
      const parsed = Papa.parse(text, { header: false, skipEmptyLines: "greedy" });
      rowsAA = parsed.data;
    }

    // Find MT5 "Positions" header row
    const hdrIdx = rowsAA.findIndex((r) => {
      const a = String(r?.[0] ?? "").trim().toLowerCase();
      const c = String(r?.[2] ?? "").trim().toLowerCase();
      const d = String(r?.[3] ?? "").trim().toLowerCase();
      const e = String(r?.[4] ?? "").trim().toLowerCase();
      return a === "time" && c === "symbol" && d === "type" && e.includes("volume");
    });

    if (hdrIdx === -1) {
      throw new Error(
        "MT5: Could not find the Positions table. Export the MT5 Trade History report (Positions)."
      );
    }

    const existingFingerprints = new Set(
      (journalRows || []).map((r) => r.importFingerprint).filter(Boolean)
    );

    const s = settings || {};
    const isPropAcct = Boolean(s.isProp || s.isPropFirm || s.isPropFunded);

    const startRB = Number(s["Running Balance"] || 0);
    const startPRB = Number(
      s["Prop Running Balance"] ??
        Math.max(0, Number(s["Initial Balance"] || 0) - Number(s["Maximum Loss Limit"] || 0))
    );

    let runningBalance = startRB;
    let propRunningBalance = startPRB;

    const toNum = (v) => parseSheetNumber(v);
    const parseDT = (v) => {
      if (v instanceof Date) return v;
      const t = String(v || "").trim();
      if (!t) return null;
      const iso = t.replace(/\./g, "-").replace(" ", "T");
      const d = new Date(iso);
      return isNaN(d.getTime()) ? null : d;
    };
    const pipSizeFor = (sym) => {
      const u = String(sym || "").toUpperCase();
      if (u.endsWith("JPY")) return 0.01;
      if (u.startsWith("XAU")) return 0.1;
      if (u.startsWith("XAG")) return 0.01;
      return 0.0001;
    };

    let imported = 0,
      skipped = 0,
      dupeArr = [];

    for (let i = hdrIdx + 1; i < rowsAA.length; i++) {
      const r = rowsAA[i];
      if (!r) continue;

      const firstCell = r[0];
      if (firstCell == null || String(firstCell).trim().toLowerCase().startsWith("orders")) break;
      if (!String(firstCell).match(/^\d{4}[.\-]\d{2}[.\-]\d{2}/)) continue;

      const entryDT = parseDT(r[0]);
      const positionId = r[1];
      const symbol = String(r[2] || "").trim();
      const sideRaw = String(r[3] || "").trim().toLowerCase();
      const volume = toNum(r[4]);
      const entryPrice = toNum(r[5]);
      const slRaw = r[6];
      const tpRaw = r[7];
      const exitDT = parseDT(r[8]);
      const exitPrice = toNum(r[9]);
      const commission = toNum(r[10]);
      const swap = toNum(r[11]);
      const profit = toNum(r[12]);

      if (!entryDT || !exitDT || !symbol) continue;

      const fp = `${formatDate(entryDT)}|${symbol}|${sideRaw}|${volume}|${entryPrice}|${exitPrice}`.toLowerCase();
      if (existingFingerprints.has(fp)) {
        skipped++;
        dupeArr.push(String(positionId || fp));
        continue;
      }

      const pipSz = pipSizeFor(symbol);
      const signedPips =
        sideRaw === "buy"
          ? (exitPrice - entryPrice) / pipSz
          : (entryPrice - exitPrice) / pipSz;
      const pips = Math.round(signedPips * 10) / 10;

      // SL/TP (pips)
      let slPips = "";
      const sl = toNum(slRaw);
      if (Number.isFinite(sl) && sl > 0) {
        const v =
          sideRaw === "buy"
            ? (entryPrice - sl) / pipSz
            : (sl - entryPrice) / pipSz;
        slPips = Math.abs(Math.round(v * 10) / 10);
      }
      let tpPips = "";
      const tp = toNum(tpRaw);
      if (Number.isFinite(tp) && tp > 0) {
        const v =
          sideRaw === "buy"
            ? (tp - entryPrice) / pipSz
            : (entryPrice - tp) / pipSz;
        tpPips = Math.abs(Math.round(v * 10) / 10);
      }

      // Actual Reward = Profit − (Commission + Swap)
      const actualReward = +(profit - (commission + swap)).toFixed(2);

      // Balances & % growth
      const beforeBal = runningBalance;
      runningBalance = +(runningBalance + actualReward).toFixed(2);
      propRunningBalance = +(propRunningBalance + actualReward).toFixed(2);
      const growthPct = beforeBal > 0 ? (actualReward / beforeBal) * 100 : 0;

      const tradeObj = {
        "Trade #": Number(positionId) || Date.now() + imported,
        Date: formatDate(entryDT),
        "Entry Time": entryDT
          ? `${String(entryDT.getHours()).padStart(2, "0")}:${String(
              entryDT.getMinutes()
            ).padStart(2, "0")}`
          : "",
        "Exit Time": exitDT
          ? `${String(exitDT.getHours()).padStart(2, "0")}:${String(
              exitDT.getMinutes()
            ).padStart(2, "0")}`
          : "",
        Duration: entryDT && exitDT ? calculateDuration(entryDT, exitDT) : "",

        Product: symbol,
        Position: sideRaw === "sell" ? "Sell" : "Buy",
        Size: Number(isFinite(volume) ? volume : 0),

        "Entry Price": Number(entryPrice || 0),
        "Exit Price": Number(exitPrice || 0),

        Pips: pips,
        "Stop Loss_Take Profit":
          slPips !== "" && tpPips !== ""
            ? `${slPips}/${tpPips}`
            : slPips !== ""
            ? `${slPips}`
            : tpPips !== ""
            ? `${tpPips}`
            : "",

        "Actual Reward": actualReward,
        "Balance Before Trade": beforeBal,
        "Actual Balance": runningBalance,
        "Actual Percent Growth": `${growthPct.toFixed(2)}%`,
        "Percent of Balance": Number(growthPct.toFixed(2)),

        Win: profit > 0,
        Loss: profit < 0,

        Notes: `Imported from MT5${positionId ? " #" + positionId : ""}`,
        created: new Date(),
        importFingerprint: fp,
      };

      await appendTrade(userId, accountId, tradeObj);
      existingFingerprints.add(fp);
      imported++;
    }

    // Persist balances
    const delta = runningBalance - startRB;
    const updates = { "settings.Running Balance": runningBalance };
    if (isPropAcct) {
      updates["settings.Prop Running Balance"] = +(startPRB + delta).toFixed(2);
    }
    await updateAccountSettings(userId, accountId, updates);

    // Keep the nice log + duplicates chip list
    setImportLog((l) => [
      `MT5 import complete. Imported: ${imported}, Skipped Duplicates: ${skipped}`,
      ...l,
    ]);
    if (dupeArr.length) setImportDuplicates(dupeArr);

    // 👈 key: give handleImport the counts so it can show the popup
    return { imported, skipped };
  }


  async function importMT4(file, journalRows) {
    try {
      if (!file) {
        setImportLog((l) => [...l, "MT4: Pick a statement file first."]);
        setImporting(false);
        return;
      }

      const text = await file.text();
      if (!text || !text.trim()) {
        setImportLog((l) => [...l, "MT4: The file was empty."]);
        setImporting(false);
        return;
      }

      const looksHtml = /<html|<table/i.test(text);
      if (!looksHtml) {
        setImportLog((l) => [
          ...l,
          "MT4: This file does not look like an MT4 HTML/XLS statement. Please export a 'Statement' (HTML) from MT4 and try again.",
        ]);
        setImporting(false);
        return;
      }

      let doc;
      try {
        const parser = new DOMParser();
        doc = parser.parseFromString(text, "text/html");
      } catch (e) {
        console.error("[MT4 import] DOMParser failed", e);
        setImportLog((l) => [...l, "MT4: Could not parse the statement file."]);
        setImporting(false);
        return;
      }

      const table = doc.querySelector("table");
      if (!table) {
        setImportLog((l) => [...l, "MT4: Could not find the main table (Closed Transactions)."]);
        setImporting(false);
        return;
      }

      const allRows = Array.from(table.querySelectorAll("tr"));

      // Locate header row: Ticket / Open Time / Type / ...
      let hdrIdx = -1;
      for (let i = 0; i < allRows.length; i++) {
        const cells = Array.from(allRows[i].querySelectorAll("td")).map((td) =>
          (td.textContent || "").trim()
        );
        if (!cells.length) continue;
        const first = (cells[0] || "").toLowerCase();
        if (first === "ticket") {
          hdrIdx = i;
          break;
        }
      }

      if (hdrIdx === -1) {
        setImportLog((l) => [
          ...l,
          "MT4: Could not find the Closed Transactions header row (Ticket / Open Time / Type / ...).",
        ]);
        setImporting(false);
        return;
      }

      const existingFingerprints = new Set(
        (journalRows || []).map((r) => r.importFingerprint).filter(Boolean)
      );

      const s = settings || {};
      const isPropAcct = Boolean(s.isProp || s.isPropFirm || s.isPropFunded);

      const startRB = Number(s["Running Balance"] || 0);
      const startPRB = Number(
        s["Prop Running Balance"] ??
          Math.max(
            0,
            Number(s["Initial Balance"] || 0) - Number(s["Maximum Loss Limit"] || 0)
          )
      );

      let runningBalance = startRB;
      let propRunningBalance = startPRB;

      const toNum = (v) => parseSheetNumber(v);
      const parseDT = (v) => {
        if (v instanceof Date) return v;
        const t = String(v || "").trim();
        if (!t) return null;
        // MT4 format: 2025.11.26 03:17:37 → 2025-11-26T03:17:37
        const m = t.match(
          /^(\d{4})\.(\d{2})\.(\d{2})\s+(\d{2}):(\d{2})(?::(\d{2}))?/
        );
        if (!m) return null;
        const [, yy, mm, dd, hh, mi, ss] = m;
        const iso = `${yy}-${mm}-${dd}T${hh}:${mi}:${ss || "00"}`;
        const d = new Date(iso);
        return isNaN(d.getTime()) ? null : d;
      };

      const pipSizeFor = (sym) => {
        const u = String(sym || "").toUpperCase();
        if (u.endsWith("JPY")) return 0.01;
        if (u.startsWith("XAU")) return 0.1;
        if (u.startsWith("XAG")) return 0.01;
        return 0.0001;
      };

      let imported = 0;
      let skipped = 0;
      const dupeArr = [];

      for (let i = hdrIdx + 1; i < allRows.length; i++) {
        const tr = allRows[i];
        const cells = Array.from(tr.querySelectorAll("td")).map((td) =>
          (td.textContent || "").trim()
        );
        if (!cells.length) continue;

        const first = cells[0] || "";

        // Stop once we leave Closed Transactions section
        if (
          /^open trades/i.test(first) ||
          /^working orders/i.test(first) ||
          /^summary:/i.test(first)
        ) {
          break;
        }

        const typeRaw = (cells[2] || "").toLowerCase();

        // Skip balance updates, deposits, summary rows, etc.
        if (
          !first ||
          typeRaw === "balance" ||
          typeRaw === "credit" ||
          typeRaw === "deposit" ||
          typeRaw === "withdrawal" ||
          first.toLowerCase().startsWith("closed p/l")
        ) {
          continue;
        }

        // Expect full closed-trade row (Ticket .. Profit)
        if (cells.length < 14) continue;

        const ticket     = first;
        const entryDT    = parseDT(cells[1]);
        const sideRaw    = typeRaw;
        const volume     = toNum(cells[3]);
        const symbol     = (cells[4] || "").toUpperCase();
        const entryPrice = toNum(cells[5]);
        const slRaw      = cells[6];
        const tpRaw      = cells[7];
        const exitDT     = parseDT(cells[8]);
        const exitPrice  = toNum(cells[9]);
        const commission = toNum(cells[10]);
        const taxes      = toNum(cells[11]);
        const swap       = toNum(cells[12]);
        const profit     = toNum(cells[13]);

        if (!entryDT || !exitDT || !symbol) continue;

        const fp = `mt4|${ticket}|${formatDate(entryDT)}|${symbol}|${sideRaw}|${volume}|${entryPrice}|${exitPrice}`.toLowerCase();
        if (existingFingerprints.has(fp)) {
          skipped++;
          dupeArr.push(String(ticket || fp));
          continue;
        }

        const pipSz = pipSizeFor(symbol);
        const signedPips =
          sideRaw === "sell"
            ? (entryPrice - exitPrice) / pipSz
            : (exitPrice - entryPrice) / pipSz;
        const pips = Math.round(signedPips * 10) / 10;

        // SL / TP (pips)
        let slPips = "";
        const sl = toNum(slRaw);
        if (Number.isFinite(sl) && sl > 0) {
          const v =
            sideRaw === "sell"
              ? (sl - entryPrice) / pipSz
              : (entryPrice - sl) / pipSz;
          slPips = Math.abs(Math.round(v * 10) / 10);
        }

        let tpPips = "";
        const tp = toNum(tpRaw);
        if (Number.isFinite(tp) && tp > 0) {
          const v =
            sideRaw === "sell"
              ? (entryPrice - tp) / pipSz
              : (tp - entryPrice) / pipSz;
          tpPips = Math.abs(Math.round(v * 10) / 10);
        }

        // Actual reward: net of commission/swap/taxes
        const actualReward = +(profit - (commission + swap + taxes)).toFixed(2);

        // Balances & % growth
        const beforeBal = runningBalance;
        runningBalance = +(runningBalance + actualReward).toFixed(2);
        propRunningBalance = +(propRunningBalance + actualReward).toFixed(2);
        const growthPct = beforeBal > 0 ? (actualReward / beforeBal) * 100 : 0;

        const tradeObj = {
          "Trade #": Number(ticket) || Date.now() + imported,
          "Date": formatDate(entryDT),
          "Entry Time": entryDT
            ? `${String(entryDT.getHours()).padStart(2, "0")}:${String(
                entryDT.getMinutes()
              ).padStart(2, "0")}`
            : "",
          "Exit Time": exitDT
            ? `${String(exitDT.getHours()).padStart(2, "0")}:${String(
                exitDT.getMinutes()
              ).padStart(2, "0")}`
            : "",
          "Duration":
            entryDT && exitDT ? calculateDuration(entryDT, exitDT) : "",

          "Product": symbol,
          "Position": sideRaw.startsWith("sell") ? "Sell" : "Buy",
          "Size": Number(Number.isFinite(volume) ? volume : 0),

          "Entry Price": Number(entryPrice || 0),
          "Exit Price": Number(exitPrice || 0),

          "Pips": pips,
          "Stop Loss_Take Profit":
            slPips !== "" && tpPips !== ""
              ? `${slPips}/${tpPips}`
              : slPips !== ""
              ? `${slPips}`
              : tpPips !== ""
              ? `${tpPips}`
              : "",

          "Actual Reward": actualReward,
          "Balance Before Trade": beforeBal,
          "Actual Balance": runningBalance,
          "Actual Percent Growth": `${growthPct.toFixed(2)}%`,
          "Percent of Balance": Number(growthPct.toFixed(2)),

          "Win": profit > 0,
          "Loss": profit < 0,

          "Notes": `Imported from MT4${ticket ? " #" + ticket : ""}`,
          "created": new Date(),
          "importFingerprint": fp,
        };

        await appendTrade(userId, accountId, tradeObj);
        existingFingerprints.add(fp);
        imported++;
      }

          // Persist balances
          const delta = runningBalance - startRB;
          const updates = { "settings.Running Balance": runningBalance };
          if (isPropAcct) {
            updates["settings.Prop Running Balance"] = +(
              startPRB + delta
            ).toFixed(2);
          }
          await updateAccountSettings(userId, accountId, updates);

          // Keep existing log + duplicates UI
          setImportLog(l => [
            `MT4 import complete. Imported: ${imported}, Skipped Duplicates: ${skipped}`,
            ...l,
          ]);
          if (dupeArr.length) setImportDuplicates(dupeArr);

          // 👇 return counts so handleImport can show the result modal
          return { imported, skipped };
        } catch (err) {
          console.error("[MT4 import] failed:", err);
          setImportLog(l => [...l, `MT4 import failed: ${err?.message || err}`]);
          // Re-throw so handleImport can show the error modal
          throw err;
        } finally {
          // Let handleImport own the spinner state
          // (optional: you can remove this line if handleImport always wraps calls)
          // setImporting(false);
        }
      }



  // ... [Keep importMannysGoogleSheet function] ...
  async function importMannysGoogleSheet(url, journalRows) {
    const spreadsheetId = url.match(/\/d\/([a-zA-Z0-9_-]+)/)?.[1];
    if (!spreadsheetId) {
      setImportLog(l => [...l, "Invalid Google Sheet URL provided"]);
      setImporting(false);
      return;
    }

    const compoundPlanUrl = `https://docs.google.com/spreadsheets/d/${spreadsheetId}/gviz/tq?tqx=out:csv&sheet=Compound%20Plan`;
    const summaryUrl      = `https://docs.google.com/spreadsheets/d/${spreadsheetId}/gviz/tq?tqx=out:csv&sheet=Summary`;

    try {
      const [compoundPlanResponse, summaryResponse] = await Promise.all([
        fetch(compoundPlanUrl),
        fetch(summaryUrl),
      ]);
      if (!compoundPlanResponse.ok || !summaryResponse.ok) {
        throw new Error("Failed to fetch one or both Google Sheet tabs. Please check sharing permissions.");
      }

      const compoundPlanCsv = await compoundPlanResponse.text();
      const summaryCsv      = await summaryResponse.text();

      const summaryData = Papa.parse(summaryCsv).data;
      const allRows     = Papa.parse(compoundPlanCsv, { header: false }).data;

      if (allRows.length < 2) throw new Error("Google Sheet 'Compound Plan' has no data rows.");

      const headers = allRows[0].map(h => (h ? String(h).trim() : ""));
      const colMap  = headers.reduce((acc, header, index) => {
        if (header) acc[header] = index;
        return acc;
      }, {});
      const dataRows = allRows.slice(1);

      const existingFingerprints = new Set(journalRows.map(r => r.importFingerprint).filter(Boolean));
      const currentSettings = settings || {};

      // Recognize all prop flags
      const isPropFirm = Boolean(
        currentSettings.isPropFirm ||
        currentSettings.isProp ||
        currentSettings.isPropFunded
      );

      let runningBalance     = Number(currentSettings["Running Balance"] || 0);
      let propRunningBalance = Number(currentSettings["Prop Running Balance"] || 0);

      // Decide if "Actual Balance (Edited by you)" matches RB or PRB more closely
      const firstSheetBalance = dataRows
        .map(r => parseSheetNumber(r[colMap["Actual Balance (Edited by you)"]]))
        .find(v => Number.isFinite(v));

      const useRBBaseline = !isPropFirm
        ? true
        : (firstSheetBalance != null &&
          Math.abs(firstSheetBalance - runningBalance) <=
          Math.abs(firstSheetBalance - propRunningBalance));

      let prevSheetBalance = useRBBaseline ? runningBalance : propRunningBalance;

      let settingsToUpdate = {};
      
      const riskVal = parseSheetNumber(findValueByLabel(summaryData, "Risk Percent"));
      if (riskVal > 0) settingsToUpdate["settings.Risk Percentage"] = riskVal; // Fixed key name!

      const tpVal = parseSheetNumber(findValueByLabel(summaryData, "Take Profit"));
      if (tpVal > 0) settingsToUpdate["settings.Take Profit"] = tpVal;

      const slVal = parseSheetNumber(findValueByLabel(summaryData, "Stop Loss"));
      if (slVal > 0) settingsToUpdate["settings.Stop Loss"] = slVal;

      if (!isPropFirm) {
        const goalVal = parseSheetNumber(findValueByLabel(summaryData, "Account Goal"));
        if (goalVal > 0) settingsToUpdate["settings.Account Goal"] = goalVal;

        const initBalVal = parseSheetNumber(findValueByLabel(summaryData, "Initial Balance"));
        if (initBalVal > 0) settingsToUpdate["settings.Initial Balance"] = initBalVal;
      }

      // Helpers (matching Settings.jsx)
      const toNum = v => Number(v || 0);
      const safeDivPct = (num, den) => (den > 0 ? (num / den) * 100 : 0);
      function blownAtBalance(initialBalance, maximumLossLimit) {
        const ib  = toNum(initialBalance);
        const mll = toNum(maximumLossLimit);
        return (ib > 0 && mll > 0) ? Math.max(mll, ib - mll) : 0;
      }
      function baseForPercents({
        prevLedgerRB, prevPropRB, initialBalance, maximumLossLimit
      }) {
        const ib  = toNum(initialBalance);
        const mll = toNum(maximumLossLimit);
        const hasProp = ib > 0 && mll > 0;
        const prevRB  = toNum(prevLedgerRB);
        const prevPRB = toNum(prevPropRB);

        if (!hasProp) return prevRB;
        if (prevPRB > 0) return prevPRB;

        const blownAt = blownAtBalance(ib, mll);
        const reconstructed = Math.max(0, prevRB - blownAt);
        const poolStart = Math.min(mll, Math.max(0, ib - mll));
        return reconstructed > 0 ? reconstructed : poolStart;
      }

      let imported = 0, skippedDuplicates = 0, skippedInvalid = 0, dupeArr = [];

      for (const row of dataRows) {
        const currentSheetBalance = parseSheetNumber(row[colMap["Actual Balance (Edited by you)"]]);
        const sheetBalanceBefore  = parseSheetNumber(row[colMap["Running Balance"]]); 
        const dateString = row[colMap["Date"]];

        if (!Number.isFinite(currentSheetBalance) || !Number.isFinite(sheetBalanceBefore) || !dateString) {
          skippedInvalid++;
          continue;
        }

        const product   = row[colMap["Pair"]] ?? "N/A";
        const position  = row[colMap["Position"]] ?? "N/A";
        const lotSize   = row[colMap["Lot Size (Max Lot Size = 1000)"]] ?? "0";
        const tradeDate = new Date(dateString);
        const fingerprint = `${formatDate(tradeDate)}-${product}-${position}-${lotSize}-${currentSheetBalance}`.toLowerCase();

        if (existingFingerprints.has(fingerprint)) {
          dupeArr.push(`Row for ${formatDate(tradeDate)} with balance ${currentSheetBalance}`);
          skippedDuplicates++;
          continue;
        }

        const actualReward = currentSheetBalance - sheetBalanceBefore;
        
        // 1. Remember the Prop Balance BEFORE the trade for accurate risk math
        const prevPropRB = propRunningBalance;
        const prevRB = runningBalance;

        // 2. Properly route the math based on what the Google Sheet is tracking
        if (useRBBaseline) {
            // Sheet is tracking the full 100k balance
            runningBalance = currentSheetBalance;
            propRunningBalance = +(propRunningBalance + actualReward).toFixed(2); // Only add the delta!
        } else {
            // Sheet is tracking the smaller Effective balance
            propRunningBalance = currentSheetBalance;
            runningBalance = +(runningBalance + actualReward).toFixed(2);
        }

        const totalRisk = Math.abs(parseSheetNumber(row[colMap["Potential Risk"]]));
        const ib  = toNum(settings?.["Initial Balance"]);
        const mll = toNum(settings?.["Maximum Loss Limit"]);
        const prevPropRBFromSheet = toNum(row[colMap["Prop Running Balance"]]);
        
        const base = baseForPercents({
          prevLedgerRB: sheetBalanceBefore,
          prevPropRB:   prevPropRBFromSheet,
          initialBalance: ib,
          maximumLossLimit: mll,
        });

        const percentGrowth      = safeDivPct(actualReward, base);
        const percentGrowthStr   = `${percentGrowth.toFixed(2)}%`;
        const thisNote           = row[colMap["Notes"]] || `Imported from Google Sheet on ${tradeDate.toISOString().slice(0, 10)}`;

        const tradeObj = {
          "Actual Balance": String(currentSheetBalance.toFixed(2)),
          "Balance Before Trade": String(sheetBalanceBefore.toFixed(2)),
          "Actual Percent Growth": percentGrowthStr,
          "Actual Reward": String(actualReward.toFixed(2)),
          "Actual Risk to Reward": (() => {
            const r = Math.abs(totalRisk);
            const R = Math.abs(actualReward);
            if (r <= 0) return R > 0 ? "∞:1" : "0:1";
            const left = Number((R / r).toFixed(2)).toString().replace(/\.?0+$/, "");
            return `${left}:1`;
          })(),

          "Date": formatDate(tradeDate),
          "Did I follow my Rules?": "",
          "Duration": "",
          "Entry Strategy": row[colMap["Strategy"]] || "",
          "Entry Time": "",
          "Exit Strategy": "",
          "Exit Time": "",
          "Grade": "",
          "Lesson": "",
          "Links_Screenshots": "",
          "LogLesson": false,
          "Loss": String(row[colMap["Loss"]]).toLowerCase() === "true",
          "Mood_Mindset": "",
          "Notes": thisNote,
          
          // 3. FIX: Calculate exact dollar budget from the EFFECTIVE balance, not the ledger balance
          "Percent of Balance": (() => {
             const targetPct = toNum(settingsToUpdate["settings.Risk Percentage"] || currentSettings?.["Risk Percentage"]);
             // Multiply against the full ledger balance to match Manny's sheet expectations
             const budgetDollars = targetPct > 0 ? (targetPct / 100) * sheetBalanceBefore : totalRisk;
             return Number(budgetDollars.toFixed(2));
          })(),

          "Position": position,
          "Product": product,
          "Risk $": "0.00",
          "Session": "",
          "Size": lotSize,
          "Stop Loss_Take Profit": `${settingsToUpdate["settings.Stop Loss"] || currentSettings?.["Stop Loss"] || ""}/${settingsToUpdate["settings.Take Profit"] || currentSettings?.["Take Profit"] || ""}`,
          "Suggested Max # of Contracts": 0,
          "Target Reward": "0.00",
          "Total Risk": String(totalRisk.toFixed(2)),
          
          "Trade #": Date.now() + imported,
          
          "Win": String(row[colMap["Win"]]).toLowerCase() === "true",
          "created": new Date(),
          "importFingerprint": fingerprint,
        };

        const pipsValue = parseSheetNumber(row[colMap["Pips"]]);
        if (Number.isFinite(pipsValue)) {
            tradeObj["Pips"] = pipsValue;
        }

        await appendTrade(userId, accountId, tradeObj);
        imported++;
      }

      if (imported > 0) {
        settingsToUpdate["settings.Running Balance"]        = runningBalance;
        settingsToUpdate["settings.Prop Running Balance"]   = propRunningBalance;
      }

      if (Object.keys(settingsToUpdate).length > 0) {
        await updateAccountSettings(userId, accountId, settingsToUpdate);
        setImportLog(l => [...l, `Account settings updated.`]);
      }

          setImportLog(l => [
            ...l,
            `Import complete. Imported: ${imported}, Skipped Duplicates: ${skippedDuplicates}, Skipped Invalid: ${skippedInvalid}`
          ]);
          if (dupeArr.length > 0) setImportDuplicates(dupeArr);

          // ✅ return counts so handleImport can show the popup
          const skipped = skippedDuplicates + skippedInvalid;
          return { ok: true, imported, skipped };
        } catch (error) {
          console.error("Error importing from Google Sheet:", error);
          let errorMessage = `Error importing: ${error.message}`;
          if (error.message.includes("Failed to fetch")) {
            errorMessage += `. Ensure that the sharing access for the Google Sheet was changed to "Anyone with the link".`;
          }
          setImportLog(l => [...l, errorMessage]);

          // ✅ return an error object so handleImport can show a failure modal
          return { ok: false, imported: 0, skipped: 0, error: errorMessage };
        } finally {
          // Let handleImport own the spinner; we still clear the URL
          // (optional: keep setImporting(false) if you prefer)
          setGoogleSheetUrl("");
        }
      }


  async function handleImport(e) {
    e.preventDefault();
    if (!userId || !accountId || !platform) return;

    setImporting(true);
    setImportLog([]);
    setImportDuplicates([]);
    let usedResultModal = false;

    try {
      if (platform === "mannysgooglesheet") {
        if (!googleSheetUrl) {
          setImportLog(l => [...l, "Please paste the Google Sheet URL first."]);
          setImporting(false);
          return;
        }

        const existing = await fetchJournalRows(userId, accountId);
        const result = await importMannysGoogleSheet(googleSheetUrl, existing);

        // Safety fallback if importer returns nothing
        if (!result) {
          setResultModal({
            ok: false,
            imported: 0,
            skipped: 0,
            error: "Google Sheet import completed without a result.",
          });
          usedResultModal = true;
          return;
        }

        const { ok = true, imported = 0, skipped = 0, error } = result;

        if (ok && imported > 0) await updateMasterBalanceFromCSV(userId, accountId);

        if (ok) {
          setResultModal({
            ok: true,
            imported,
            skipped,
            note: "Imported from Manny’s Compound Plan Google Sheet.",
          });
        } else {
          setResultModal({
            ok: false,
            imported: 0,
            skipped: 0,
            error: error || "Google Sheet import failed.",
          });
        }

        usedResultModal = true;
        return;
      } else if (platform === "mt5") {
        // MT5 – show modal
        if (!file) {
          setImportLog(l => [...l, "Pick a CSV or XLSX file to import."]);
          return;
        }

        const existing = await fetchJournalRows(userId, accountId);
        const { imported = 0, skipped = 0 } = await importMT5(file, existing);

        if (imported > 0) await updateMasterBalanceFromCSV(userId, accountId);

        const note = "MT5 imports read the 'Positions' table from your MT5 Trade History report.";
        setImportLog(l => [...l, `Import complete. Imported: ${imported}, Skipped: ${skipped}`]);
        setResultModal({ ok: true, imported, skipped, note });
        usedResultModal = true;
      } else if (platform === "mt4") {
        // MT4 – show modal
        if (!file) {
          setImportLog(l => [...l, "Pick an MT4 statement file to import."]);
          return;
        }

        const existing = await fetchJournalRows(userId, accountId);
        const { imported = 0, skipped = 0 } = await importMT4(file, existing);

        if (imported > 0) await updateMasterBalanceFromCSV(userId, accountId);

        const note = "MT4 imports use the standard MT4 Statement export.";
        setImportLog(l => [...l, `Import complete. Imported: ${imported}, Skipped: ${skipped}`]);
        setResultModal({ ok: true, imported, skipped, note });
        usedResultModal = true;
      } else if (platform === "tradingview_strategy") {
        
        if (!file) {
          setImportLog(l => [...l, "Pick a TradingView Strategy CSV file to import."]);
          return;
        }

        const text = await file.text();
        const convertedTrades = convertTradingViewToTrapound(text, tvConfig);
        
        const existing = await fetchJournalRows(userId, accountId);
        const existingFingerprints = new Set(existing.map(r => r.importFingerprint).filter(Boolean));
        
        let imported = 0;
        let skipped = 0;

        for (const trade of convertedTrades) {
            // Create a unique fingerprint to prevent duplicates
            const fp = `tvstrat|${trade.Date}|${trade["Entry Time"]}|${trade["Exit Time"]}|${trade.Product}|${trade.Position}`.toLowerCase();
            
            if (existingFingerprints.has(fp)) {
                skipped++;
                continue;
            }

            trade.importFingerprint = fp;
            trade.created = new Date();
            await appendTrade(userId, accountId, trade);
            existingFingerprints.add(fp);
            imported++;
        }

        if (imported > 0) await updateMasterBalanceFromCSV(userId, accountId);

        setImportLog(l => [...l, `Import complete. Imported: ${imported}, Skipped: ${skipped}`]);
        setResultModal({ ok: true, imported, skipped, note: "TradingView Strategy import complete." });
        usedResultModal = true;

      } else {
        // CSV flow (TopStep, Tradovate, Rithmic, Quantower, etc.)
        if (!file) {
          setImportLog(l => [...l, "Pick a CSV file to import."]);
          return;
        }

        const result = await importCsvByPlatform({
          userId,
          accountId,
          platform,
          file,
          finalBalance,
        });

        if (!result) {
          throw new Error(`Importer for ${platform} did not return { imported, skipped }`);
        }

        const { imported = 0, skipped = 0 } = result;

        if (imported > 0) await updateMasterBalanceFromCSV(userId, accountId);
        const note =
          platform === "rithmic"
            ? "Rithmic imports use the Positions and Fills sections."
            : "";

        setImportLog(l => [...l, `Import complete. Imported: ${imported}, Skipped: ${skipped}`]);
        setResultModal({ ok: true, imported, skipped, note });
        usedResultModal = true;
      }
    } catch (err) {
      console.error(err);
      const msg = err?.message || "Import failed";
      setImportLog(l => [...l, msg]);
      setResultModal({ ok: false, imported: 0, skipped: 0, error: msg });
      usedResultModal = true;
    } finally {
      setImporting(false);
      // If a result modal was used, user will click "Done" / "OK" there.
    }
  }

  // --- RENDER ---

  if (!userId || !accountId) {
    return (
      <div className="p-4 text-gray-600 dark:text-gray-300">
        Select an account to enable CSV import.
      </div>
    );
  }

  // 1. If NO platform selected, show the GRID
  if (!platform) {
    return (
      <div className="p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
        <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <FaFileCsv className="text-blue-600" />
          Select Platform
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
          Choose the platform you are importing trades from.
        </p>
        
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {availableOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setPlatform(opt.value)}
              className="group relative flex flex-col items-center justify-center h-32 p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 hover:bg-white dark:hover:bg-gray-700 hover:shadow-lg dark:hover:shadow-black/40 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {/* Logo/Icon Container */}
              <div className="flex-1 w-full flex items-center justify-center mb-2">
                <PlatformLogo platform={opt} className="transition-transform group-hover:scale-105" />
              </div>
              {/* Label */}
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-blue-600 dark:group-hover:text-blue-400">
                {opt.label}
              </span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // 2. If platform SELECTED, show the FORM (with Back button)
  const selectedPlatformLabel = availableOptions.find(p => p.value === platform)?.label || platform;

  return (
    <div className="border rounded-xl p-6 bg-white dark:bg-gray-800 dark:border-gray-700">
      {/* Header with Back Button */}
      <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100 dark:border-gray-700">
        <button
          onClick={() => {
            setPlatform("");
            setImportLog([]);
            setFile(null);
            setGoogleSheetUrl("");
          }}
          className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition"
          title="Back to platforms"
        >
          <FaArrowLeft />
        </button>
        <div>
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
            Import from {selectedPlatformLabel}
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Upload your CSV/Excel file below
          </p>
        </div>
      </div>

      <form onSubmit={handleImport} className="space-y-5">
        
        {/* Manny's Google Sheet instructions + URL */}
        {isMannyGoogleSheet && (
          <div className="space-y-4 animate-fadeIn">
            <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 flex gap-3">
              <FaExclamationTriangle className="mt-0.5 text-blue-500 shrink-0" />
              <div className="text-sm text-blue-800 dark:text-blue-200">
                <p className="font-semibold mb-1">Before importing:</p>
                <ul className="list-disc list-inside space-y-1 opacity-90">
                  <li>In the <b>Compound Plan</b> tab, remove the first 8 header lines.</li>
                  <li>Set sharing access to <b>"Anyone with the link"</b>.</li>
                  <li>Ensure each trade has a valid Date.</li>
                </ul>
              </div>
            </div>

            <div>
              <label className="block mb-1.5 font-semibold text-gray-700 dark:text-gray-200">
                Google Sheet URL
              </label>
              <div className="relative">
                <FaGlobeAmericas className="absolute left-3 top-3 text-gray-400" />
                <input
                  type="text"
                  value={googleSheetUrl}
                  onChange={(e) => setGoogleSheetUrl(e.target.value)}
                  placeholder="https://docs.google.com/spreadsheets/d/..."
                  required
                  className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition"
                />
              </div>
            </div>
          </div>
        )}

        {/* CSV/XLSX File Input (for non-Manny) */}
        {!isMannyGoogleSheet && !isTVStrategy && (
          <div className="animate-fadeIn space-y-4">
            <div>
              <label className="block mb-1.5 font-semibold text-gray-700 dark:text-gray-200">
                File Upload ({acceptTypes.replace(/\./g, "").toUpperCase()})
              </label>
              <div className="relative group">
                <input
                  type="file"
                  accept={acceptTypes}
                  required
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-gray-500 dark:text-gray-400
                    file:mr-4 file:py-2.5 file:px-4
                    file:rounded-lg file:border-0
                    file:text-sm file:font-semibold
                    file:bg-blue-50 file:text-blue-700
                    hover:file:bg-blue-100
                    dark:file:bg-gray-700 dark:file:text-gray-200
                    dark:hover:file:bg-gray-600
                    cursor-pointer focus:outline-none border border-gray-300 dark:border-gray-600 rounded-lg p-1"
                />
              </div>
            </div>

            {/* Final balance (TopStep/Tradovate/Ninja only) */}
            {["topstep", "tradovate", "ninjatrader"].includes(platform) && (
              <div>
                <label className="block mb-1.5 font-semibold text-gray-700 dark:text-gray-200">
                  Ending Account Balance <span className="font-normal text-gray-500 text-xs">(Optional)</span>
                </label>
                <input
                  type="number"
                  value={finalBalance}
                  onChange={(e) => setFinalBalance(e.target.value)}
                  placeholder="Leave blank to calculate automatically"
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition no-spin"
                />
              </div>
            )}
          </div>
        )}

        {/* TradingView Strategy Configuration */}
                {isTVStrategy && (
                  <div className="space-y-4 animate-fadeIn">
                    <div className="p-4 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 flex gap-3">
                      <FaChartLine className="mt-0.5 text-indigo-500 shrink-0" />
                      <div className="text-sm text-indigo-800 dark:text-indigo-200">
                        <p className="font-semibold mb-1">TradingView Strategy Setup</p>
                        <p className="opacity-90">Enter your strategy configuration below before uploading your "List of Trades" CSV.</p>
                      </div>
                    </div>

                    {/* CSV File Input */}
                    <div>
                      <label className="block mb-1.5 font-semibold text-gray-700 dark:text-gray-200">
                        Strategy CSV File
                      </label>
                      <input
                        type="file"
                        accept=".csv"
                        required
                        onChange={(e) => setFile(e.target.files?.[0] || null)}
                        className="block w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2.5 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 dark:file:bg-gray-700 dark:file:text-gray-200 cursor-pointer border border-gray-300 dark:border-gray-600 rounded-lg p-1"
                      />
                    </div>

                    {/* Strategy Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Product</label>
                          <input type="text" placeholder="e.g., MNQ, EURUSD" value={tvConfig.product} onChange={(e) => setTvConfig({...tvConfig, product: e.target.value})} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" required />
                      </div>
                      <div>
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Account Type</label>
                          <select value={tvConfig.accountType} onChange={(e) => setTvConfig({...tvConfig, accountType: e.target.value})} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                              <option value="Futures">Futures</option>
                              <option value="Forex">Forex</option>
                          </select>
                      </div>
                      <div>
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Starting Balance</label>
                          <input type="number" step="any" value={tvConfig.startingBalance} onChange={(e) => setTvConfig({...tvConfig, startingBalance: e.target.value})} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" required />
                      </div>
                      <div>
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Timeframe</label>
                          <input type="text" placeholder="e.g., 5m" value={tvConfig.timeframe} onChange={(e) => setTvConfig({...tvConfig, timeframe: e.target.value})} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                      </div>
                      <div>
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Entry Strategy</label>
                          <input type="text" value={tvConfig.entryStrategy} onChange={(e) => setTvConfig({...tvConfig, entryStrategy: e.target.value})} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                      </div>
                      <div>
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Exit Strategy</label>
                          <input type="text" value={tvConfig.exitStrategy} onChange={(e) => setTvConfig({...tvConfig, exitStrategy: e.target.value})} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                      </div>
                    </div>
                  </div>
                )}

        {/* Submit Button */}
        <div className="pt-2">
          <button
            type="submit"
            disabled={importing}
            className={`w-full py-3 rounded-xl font-bold text-white shadow-md transition-all transform active:scale-[0.99]
              ${importing 
                ? "bg-gray-400 cursor-not-allowed" 
                : "bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"}`}
          >
            {importing ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path></svg>
                Processing...
              </span>
            ) : (
              `Import Trades`
            )}
          </button>
        </div>
      </form>

      {showTVModal && (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md p-6 border border-gray-200 dark:border-gray-700">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">TradingView Strategy Import</h3>
            
            <div className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">CSV File</label>
                    <input 
                        type="file" 
                        accept=".csv"
                        onChange={(e) => setTvFile(e.target.files[0])} 
                        className="w-full text-sm dark:text-gray-300"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Product</label>
                    <input 
                        type="text" 
                        placeholder="e.g., MNQ, EURUSD"
                        value={tvConfig.product} 
                        onChange={(e) => setTvConfig({...tvConfig, product: e.target.value})} 
                        className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700"
                    />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Entry Strategy</label>
                        <input 
                            type="text" 
                            value={tvConfig.entryStrategy} 
                            onChange={(e) => setTvConfig({...tvConfig, entryStrategy: e.target.value})} 
                            className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Exit Strategy</label>
                        <input 
                            type="text" 
                            value={tvConfig.exitStrategy} 
                            onChange={(e) => setTvConfig({...tvConfig, exitStrategy: e.target.value})} 
                            className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700"
                        />
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Timeframe</label>
                        <input 
                            type="text" 
                            placeholder="e.g., 5m"
                            value={tvConfig.timeframe} 
                            onChange={(e) => setTvConfig({...tvConfig, timeframe: e.target.value})} 
                            className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Starting Balance</label>
                        <input 
                            type="number" 
                            value={tvConfig.startingBalance} 
                            onChange={(e) => setTvConfig({...tvConfig, startingBalance: e.target.value})} 
                            className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700"
                        />
                    </div>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Account Type</label>
                    <select 
                        value={tvConfig.accountType} 
                        onChange={(e) => setTvConfig({...tvConfig, accountType: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700"
                    >
                        <option value="Futures">Futures</option>
                        <option value="Forex">Forex</option>
                        <option value="Stock">Stock</option>
                    </select>
                </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
                <button 
                    onClick={() => setShowTVModal(false)}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 font-bold rounded-lg hover:bg-gray-300"
                >
                    Cancel
                </button>
                <button 
                    onClick={handleTVImport}
                    className="px-4 py-2 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700"
                >
                    Process & Import
                </button>
            </div>
        </div>
    </div>
)}

      {/* Log */}
      {importLog.length > 0 && (
        <div className="mt-6 p-4 rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-sm max-h-40 overflow-y-auto custom-scroll">
          <p className="font-bold text-gray-700 dark:text-gray-200 mb-2 sticky top-0 bg-gray-50 dark:bg-transparent">Log:</p>
          {importLog.map((log, i) => (
            <div key={i} className="text-gray-600 dark:text-gray-300 border-b border-gray-100 dark:border-gray-700/50 last:border-0 py-1">
              {log}
            </div>
          ))}
          {importDuplicates.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
              <p className="font-semibold text-amber-600 dark:text-amber-400 text-xs uppercase tracking-wide mb-1">Skipped Duplicates:</p>
              <div className="flex flex-wrap gap-1">
                {importDuplicates.slice(0, 10).map((d, i) => (
                   <span key={i} className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 text-xs rounded-full">
                     {d}
                   </span>
                ))}
                {importDuplicates.length > 10 && <span className="text-xs text-gray-500">...and {importDuplicates.length - 10} more</span>}
              </div>
            </div>
          )}
        </div>
      )}

      <ImportResultModal
        open={!!resultModal}
        result={resultModal}
        onAcknowledge={async () => {
          setResultModal(null);
          if (typeof onDone === "function") await onDone();
        }}
        onDismiss={() => setResultModal(null)}
      />
    </div>
  );
}